import { useEffect, useMemo, useState, useContext } from 'react';
import { useRemote } from '../../../Utils/Utils';
import { Search } from 'react-feather';
import { BannerContext } from '../../../InternalApiApp';
import Table from '../../../components/Table/Table';
import LoadingIcon from '../../Shared/LoadingIcon';
import SimpleSearchFilter, { filterBy } from '../../../components/Input/SimpleSearchFilter';
import FlexWrapper from '../../FlexWrapper';
import TruncatedString from '../../Shared/TruncatedString';
import AnalyticsExportButton from './AnalyticsExportButton';
import { analyticsExportFilename } from '../AnalyticsUtils';
import moment from 'moment';


const SequentialPortfolioViews = ({ timeFrame, customeDate }) => {
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState([])
    const [hasSearchTerm, setHasSearchTerm] = useState("")

    const { bannerAdditionalInfo } = useContext(BannerContext)
    const remote = useRemote();

    useEffect(() => {
        if (bannerAdditionalInfo && customeDate && customeDate.from && customeDate.to) {
            setLoading(true);

            const end = moment(customeDate.to).format('YYYY-MM-DD');
            const start = moment(customeDate.from).format('YYYY-MM-DD');
            const products = bannerAdditionalInfo?.platforms.map(platform =>
                platform.products.map(p => p.selected ? `product=${p.id}` : null)
                    .filter(e => e !== null)
                    .join("&")
            )

            const dataPromise = remote.get(`/analytics/aggregates/sequential-portfolio-views?${products ? products + "&" : ""}from=${start}&to=${end}&scope=Manager`)

            Promise.all([dataPromise]).then(([fetchedData]) => {
                const sortedRankings = fetchedData.length > 0 ? fetchedData.sort((a, b) => b.sessionCount - a.sessionCount) : [];
                const totalCount = fetchedData.length > 0 ? fetchedData.reduce((acc, curr) => acc + curr.sessionCount, 0) : 0;
                const rankedData = sortedRankings.map((portfolio) => ({
                    ...portfolio,
                    sharePercentage: totalCount > 0 ? ((portfolio.sessionCount / totalCount) * 100)/*.toFixed(2)*/ : '0.00'
                }))
                setData(rankedData)
                setLoading(false);
            })
        }
    }, [timeFrame, customeDate, bannerAdditionalInfo, remote]);

    const colWidths = ["auto", "auto", "90px", "90px"]
    const headers = [
        {
            text: "From",
            className: 'compact-header'
        },
        {
            text: "To",
            className: 'compact-header'
        },
        {
            text: "Count",
            className: 'text-align-right compact-header'
        },
        {
            text: "% of Total",
            className: 'text-align-right compact-header'
        }
    ]

    function dataMap(data) {
        const mappedData = data && data.map((sequence, idx) => {
            if (idx > 9) return null;
            return (
                <>
                    <tr key={idx} >
                        <td style={{ fontSize: '0.85rem', paddingRight: '1rem' }}><TruncatedString string={sequence.fromName} /></td>
                        <td style={{ fontSize: '0.85rem', paddingRight: '1rem' }}><TruncatedString string={sequence.toName} /></td>
                        <td style={{ fontSize: '0.85rem', textAlign: 'right' }}>{sequence.sessionCount}</td>
                        <td style={{ fontSize: '0.85rem', textAlign: 'right' }}>{sequence.sharePercentage.toFixed(2)}</td>
                    </tr>
                </>
            );
        })
        return mappedData;
    };

    function filter(dataToFilter, searchTerm) {
        if (searchTerm === null || searchTerm.trim().length === 0) {
            return dataToFilter
        }
        return dataToFilter.filter((datum) => filterBy(datum, ["fromName", "toName"], searchTerm))
    }

    const filteredData = filter(data, hasSearchTerm)
    const dataToDisplay = !!filteredData ? dataMap(filteredData) : null

    const exportHeaders = useMemo(() => [
        { label: 'From', key: 'fromName' },
        { label: 'To', key: 'toName' },
        { label: 'Count', key: 'sessionCount' },
        { label: '% of Total', key: 'sharePercentageFormatted' },
    ], []);

    const exportData = useMemo(() => data.map(r => ({
        fromName: r.fromName,
        toName: r.toName,
        sessionCount: r.sessionCount,
        sharePercentageFormatted: typeof r.sharePercentage === 'number' ? r.sharePercentage.toFixed(2) : r.sharePercentage,
    })), [data]);

    const exportFilename = useMemo(
        () => analyticsExportFilename('portfolio-hops', customeDate),
        [customeDate]
    );

    return (
        <>
            <FlexWrapper className='mb-s' justify="space-between" >
                <div className='mb-s ' style={{ lineHeight: '1' }}>Most frequent portfolio hops per session.</div>
                <FlexWrapper gap="s">
                    <SimpleSearchFilter
                        placeholder={`Search portfolios`}
                        setSearchTerm={setHasSearchTerm}
                        size="small"
                        width="m"
                        icon={<Search />}
                        searchTerm={hasSearchTerm}
                    />
                    <AnalyticsExportButton headers={exportHeaders} data={exportData} filename={exportFilename} loading={loading} />
                </FlexWrapper>
            </FlexWrapper>
            {loading
                ? <div style={{ height: 352, display: 'flex' }}><LoadingIcon centered /></div>
                : <Table
                    suggestionMessage
                    suggestionMessageText={"No transitions in this period. Try expanding the timeframe."}
                    smStyle={{ height: 352 }}
                    // height={352}
                    rowSize="s"
                    colWidths={colWidths}
                    headers={headers}
                    dataMap={dataToDisplay} />
            }
        </>
    )
}

export default SequentialPortfolioViews